import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../desktop/components/firebase';
import { onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import axios from 'axios';

// Configure global Axios request interceptor to attach Firebase ID tokens
axios.interceptors.request.use(async (config) => {
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      const devBypass = process.env.REACT_APP_DEV_NO_AUTH === '1';
      if (devBypass) {
        config.headers.Authorization = 'Bearer dev-mock-token';
      }
    }
  } catch (error) {
    console.error('Error in Axios auth interceptor:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

const FirebaseAuthContext = createContext(null);

export const FirebaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Standard Firebase Auth state listener
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
      } else {
        // If in development mode and bypass is preferred, auto-populate a mock user
        const devBypass = process.env.REACT_APP_DEV_NO_AUTH === '1';
        if (devBypass) {
          console.log('FirebaseAuthProvider: Dev bypass active. Populating mock user.');
          setUser({
            uid: 'dev-user',
            email: 'dev@example.com',
            displayName: 'Dev User',
            getIdToken: async () => 'dev-mock-token'
          });
        } else {
          setUser(null);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await fbSignOut(auth);
    } catch (error) {
      console.error('Firebase sign out error:', error);
    } finally {
      setUser(null);
    }
  };

  const getToken = async () => {
    if (!user) return null;
    try {
      if (user.getIdToken) {
        return await user.getIdToken();
      }
      return 'dev-mock-token';
    } catch (error) {
      console.error('Error getting Firebase ID token:', error);
      return null;
    }
  };

  return (
    <FirebaseAuthContext.Provider value={{ user, loading, signOut, getToken }}>
      {!loading && children}
    </FirebaseAuthContext.Provider>
  );
};

export const useFirebaseAuth = () => {
  const context = useContext(FirebaseAuthContext);
  if (!context) {
    throw new Error('useFirebaseAuth must be used within a FirebaseAuthProvider');
  }
  return context;
};
